import { useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Queries } from '@shared/api';
import { Field } from '@shared/data/entity/fields/Field';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { useProfile } from '@shared/util/hooks';
import { FieldRecord, GetUpdatedField, UpdateField } from '../../types';
import { buildFieldRecord } from '../../util/buildFieldRecord';
import { getModifiedFields, isFieldModified } from '../../util/isFieldRecordModified';

export interface EditFieldsHook {
  fields: FieldRecord;
  originalFields: FieldRecord | null;
  modifiedFields: FieldRecord | null;
  threadId?: string;
  errors: CardEntityError[];
  loading: boolean;
  isDirty: boolean;
  updateField: UpdateField;
}

export function useEditFields(
  entityId: string,
  workflowTemplate?: MBAPI.WorkflowTemplate,
): EditFieldsHook {
  const { data, loading, error } = useQuery<
    MBAPI.GetWorkflowEntityQuery,
    MBAPI.GetWorkflowEntityQueryVariables
  >(Queries.workflow.getWorkflowEntity, {
    variables: { input: { id: entityId } },
    skip: isNil(entityId),
  });
  const { profile } = useProfile();
  const [originalFields, setOriginalFields] = useState<FieldRecord | null>(null);
  const [fields, setFields] = useState<FieldRecord>({});
  const modifiedFields = useMemo(
    () => getModifiedFields(originalFields, fields),
    [originalFields, fields],
  );

  useEffect(() => {
    if (entityId) {
      setFields({});
      setOriginalFields(null);
    }
  }, [entityId]);

  const updateField = (componentId: string, field: Field | null | GetUpdatedField) => {
    setFields(previousFields => {
      const previousField = previousFields[componentId];

      let updatedField: Field | null;
      if (typeof field === 'function') {
        updatedField = field(previousField);
      } else {
        updatedField = field;
      }

      if (isFieldModified(previousField, updatedField)) {
        return { ...previousFields, [componentId]: updatedField };
      } else {
        return previousFields;
      }
    });
  };

  const isDirty = !isNil(modifiedFields) && Object.keys(modifiedFields).length > 0;

  useEffect(() => {
    const entity = data?.workflowEntity?.entity;
    const refs = data?.workflowEntity?.refs;
    const components = workflowTemplate?.components;
    const fieldsHaveBeenSet = !isEmpty(fields) || !isEmpty(originalFields);
    if (entity && refs && components && !fieldsHaveBeenSet) {
      const fieldRecord = buildFieldRecord({
        entity,
        components,
        refs,
        currentUser: profile,
      });
      setOriginalFields(fieldRecord);
      setFields(fieldRecord);
    }
  }, [data, fields, originalFields, workflowTemplate?.components, profile]);

  return {
    fields,
    originalFields,
    modifiedFields,
    threadId: data?.workflowEntity?.entity.threadId,
    updateField,
    loading,
    isDirty,
    errors: !isNil(error) ? [{ description: error.message }] : [],
  };
}
